/**
 * AvailableHoursSheet — the managers' forum request of 2026-09-24.
 *
 * "Who can still work this, without going over forty?" Officers with room
 * under the threshold across EVERY job, filtered to the post in front of you:
 * eligible, qualified, and not already working that window. Sorted by hours
 * remaining, most available first. One tap assigns, and it goes through the
 * same path a cell does - so the same pop-ups still ask their questions.
 *
 * Hours only. This panel has no access to a rate and no reason to want one.
 */
import React, { useEffect, useState } from 'react';
import { schedulingAPI } from '../../lib/api';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '../ui/sheet';
import { Badge } from '../ui/badge';
import { Clock } from 'lucide-react';

const hhmm = (v) => (v || '').slice(0, 5);

export function AvailableHoursSheet({ open, onOpenChange, weekStart, jobId, cell, onChoose }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [threshold, setThreshold] = useState(40);

  useEffect(() => {
    if (!open) return;
    let live = true;
    setLoading(true);
    schedulingAPI.availableHours({
      week_start_date: weekStart,
      job_id: jobId,
      job_post_id: cell?.postId || undefined,
      shift_date: cell?.date || undefined,
      time_in: cell?.timeIn || undefined,
      time_out: cell?.timeOut || undefined,
      next_day: cell?.nextDay || false,
    }).then(({ data }) => {
      if (!live) return;
      setRows(data.officers || []);
      setThreshold(data.threshold_hours ?? 40);
    }).catch(() => live && setRows([]))
      .finally(() => live && setLoading(false));
    return () => { live = false; };
  }, [open, weekStart, jobId, cell?.postId, cell?.date, cell?.timeIn, cell?.timeOut, cell?.nextDay]);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-md overflow-y-auto"
        data-testid="available-hours-sheet">
        <SheetHeader>
          <SheetTitle>Who has hours left</SheetTitle>
          <SheetDescription className="text-sm leading-relaxed">
            {cell?.postName
              ? `Officers who can take ${cell.postName} on ${cell.date}, `
                + `${hhmm(cell.timeIn)}–${hhmm(cell.timeOut)} — qualified, free at that time, `
                + `and still under ${threshold} hours across every job.`
              : `Officers still under ${threshold} hours this week, across every job.`}
          </SheetDescription>
        </SheetHeader>

        <div className="mt-4 space-y-1">
          {loading ? (
            <p className="text-sm text-fg-muted" data-testid="available-hours-loading">Looking…</p>
          ) : !rows.length ? (
            <p className="text-sm text-fg-muted" data-testid="available-hours-empty">
              Nobody in your access has room under {threshold} hours for this shift.
            </p>
          ) : rows.map((r) => (
            <button key={r.employee_id} type="button"
              data-testid={`available-hours-assign-${r.employee_id}`}
              onClick={() => onChoose(r)}
              className="w-full text-left rounded-lg border border-line px-3 py-2
                         hover:border-primary hover:bg-surface-sunken transition-colors">
              <div className="flex items-center justify-between gap-2">
                <span className="text-sm text-fg truncate">{r.display_last_first}</span>
                <span className="text-xs text-fg-muted tabular-nums flex items-center gap-1">
                  <Clock className="h-3 w-3" />{r.hours_remaining}h left
                </span>
              </div>
              <div className="flex flex-wrap items-center gap-1.5 mt-1">
                <span className="text-[11px] text-fg-muted tabular-nums">
                  {r.hours_this_week_all_jobs}h scheduled
                </span>
                {r.is_assigned_to_job && (
                  <Badge variant="outline" className="text-[10px] py-0">On this job</Badge>
                )}
                {(r.jobs_this_week || []).length > 1 && (
                  <span className="text-[11px] text-fg-muted">
                    {r.jobs_this_week.join(', ')}
                  </span>
                )}
              </div>
            </button>
          ))}
        </div>
      </SheetContent>
    </Sheet>
  );
}
